import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { GraphicEq as GraphicEqIcon, RecordVoiceOver as RecordVoiceOverIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { exercisesApi, type TextTtsEstimate } from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'
import { SectionLabel, StringListEditor } from './primitives'
import type { DictationContent } from '../contentTypes'

/**
 * Редактор диктанта: обычный тип задания, а не ссылка на отдельную сущность.
 *
 * Озвучка вынесена в явную кнопку и не делается при сохранении: синтез платный
 * и списывается с баланса учителя, а сохранение формы учитель жмёт по десять
 * раз подряд — побочным эффектом это списало бы деньги за каждую правку.
 */

type Props = {
  content: DictationContent
  onChange: (content: DictationContent) => void
  disabled?: boolean
  /** id сохранённого задания: озвучивается текст с сервера, у нового задания его ещё нет */
  exerciseId?: number | null
}

/** Варианты скорости: строкой, как их принимает сервер (decimal 0.50–1.50) */
const PLAYBACK_RATES = ['1.00', '0.90', '0.75'] as const

/**
 * Потолок прослушиваний — зеркало серверного `config('exercises.dictation.max_plays_cap')`.
 * Форма предлагала до 20, а сервер резал на 10: сохранение падало 422 на числе,
 * которое сама же форма и подсказала.
 */
const MAX_PLAYS_CAP = 10

export function DictationEditor({ content, onChange, disabled, exerciseId = null }: Props) {
  const { t } = useTranslation()

  const audio = content.audio ?? { tts_audio_id: null, path: null, url: null, status: 'none' as const }
  const accepted = content.accepted_answers ?? []

  const [estimate, setEstimate] = useState<TextTtsEstimate | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioReset, setAudioReset] = useState(false)

  const sourceText = content.source_text ?? ''

  /**
   * Опорные тексты: `server` — то, что лежит на сервере (озвучка идёт по нему,
   * а не по набранному в форме), `own` — последнее, что редактор сам положил в
   * контент.
   *
   * Сравниваем именно с `own`: диалог задания переиспользуется, контент
   * следующего диктанта приходит уже после монтирования, и опорный текст,
   * снятый один раз на первом рендере, оставался бы текстом предыдущего
   * задания — над кнопкой висело бы ложное «текст не сохранён».
   */
  const [baseline, setBaseline] = useState({ server: sourceText, own: sourceText })

  if (sourceText !== baseline.own) {
    // Текст сменился не нашей правкой — значит, в форму подали другое задание,
    // и пришедший текст по определению серверный
    setBaseline({ server: sourceText, own: sourceText })
  }

  const unsavedText = sourceText.trim() !== baseline.server.trim()

  const changeSourceText = (value: string) => {
    // Запись относится к прежней фразе: оставить её значит выдать ученику звук,
    // не совпадающий с эталоном, и засчитать ему ошибку за чужой текст
    const audioStale = audio.status !== 'none'
    setEstimate(null)
    if (audioStale) setAudioReset(true)
    setBaseline((prev) => ({ ...prev, own: value }))

    onChange({
      ...content,
      source_text: value,
      audio: audioStale ? { tts_audio_id: null, path: null, url: null, status: 'none' } : audio,
    })
  }

  const showPrice = async () => {
    if (sourceText.trim() === '') return
    setError(null)
    setBusy(true)
    try {
      setEstimate(await exercisesApi.estimateTextTts(sourceText))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  const synthesize = async () => {
    // Несохранённый текст — не предупреждение, а запрет: озвучится прежняя
    // фраза, а деньги спишутся как за новую
    if (exerciseId === null || unsavedText) return
    setError(null)
    setBusy(true)
    try {
      const updated = await exercisesApi.synthesize(exerciseId)
      const serverContent = (updated.content ?? {}) as Partial<DictationContent>

      // Берём из ответа только блок audio: остальные поля в форме могли уйти
      // вперёд (учитель поправил лимит прослушиваний, добавил вариант ответа),
      // и целиком серверный content затирал бы эти правки без предупреждения
      onChange({ ...content, audio: serverContent.audio ?? audio })

      const serverText = serverContent.source_text
      if (typeof serverText === 'string') {
        setBaseline((prev) => ({ ...prev, server: serverText }))
      }

      setAudioReset(false)
      setEstimate(null)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        label={t('exercises.editor.dictationSourceText', 'Эталонный текст')}
        helperText={t('exercises.editor.dictationSourceTextHint', 'Его озвучит синтез речи, и его же ученик должен записать')}
        value={sourceText}
        disabled={disabled}
        onChange={(event) => changeSourceText(event.target.value)}
      />

      <StringListEditor
        label={t('exercises.editor.dictationAccepted', 'Другие принимаемые написания')}
        values={accepted}
        minItems={0}
        multiline
        disabled={disabled}
        addLabel={t('exercises.editor.dictationAcceptedAdd', 'Добавить вариант')}
        onChange={(values) => onChange({ ...content, accepted_answers: values })}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
        {t(
          'exercises.editor.dictationAcceptedHint',
          'Вариант пишется целиком, а не одним словом: ответ сверяется со всей фразой',
        )}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          size="small"
          type="number"
          sx={{ maxWidth: 220 }}
          label={t('exercises.editor.maxPlays')}
          helperText={t('exercises.editor.maxPlaysHint', 'От 1 до {{max}}', { max: MAX_PLAYS_CAP })}
          inputProps={{ min: 1, max: MAX_PLAYS_CAP }}
          value={content.max_plays ?? ''}
          disabled={disabled}
          onChange={(event) =>
            onChange({ ...content, max_plays: event.target.value === '' ? null : Number(event.target.value) })
          }
        />

        <TextField
          select
          size="small"
          sx={{ minWidth: 220 }}
          label={t('exercises.editor.dictationPlaybackRate', 'Скорость воспроизведения')}
          value={String(content.playback_rate ?? '1.00')}
          disabled={disabled}
          onChange={(event) => onChange({ ...content, playback_rate: event.target.value })}
        >
          {PLAYBACK_RATES.map((rate) => (
            <MenuItem key={rate} value={rate}>
              {/* Точка в ключе — разделитель уровней i18next: «1.00» ушло бы
                  искать вложенный объект, поэтому в ключе подчёркивание */}
              {t(`exercises.editor.dictationRate.${rate.replace('.', '_')}`, `×${rate}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControlLabel
          control={
            <Switch
              checked={content.ignore_case !== false}
              disabled={disabled}
              onChange={(event) => onChange({ ...content, ignore_case: event.target.checked })}
            />
          }
          label={t('exercises.editor.dictationIgnoreCase', 'Не учитывать регистр')}
        />
        <FormControlLabel
          control={
            <Switch
              checked={content.ignore_punctuation !== false}
              disabled={disabled}
              onChange={(event) => onChange({ ...content, ignore_punctuation: event.target.checked })}
            />
          }
          label={t('exercises.editor.dictationIgnorePunctuation', 'Не учитывать пунктуацию')}
        />
      </Stack>

      <Divider />

      <Box>
        <SectionLabel
          text={t('exercises.editor.dictationVoice', 'Озвучка')}
          hint={t(
            'exercises.editor.dictationVoiceHint',
            'Синтез платный и списывается с вашего баланса, поэтому это отдельная кнопка, а не часть сохранения',
          )}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {audioReset && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t(
              'exercises.editor.dictationAudioReset',
              'Текст изменён — прежняя запись сброшена. Озвучьте заново, иначе ученик останется без звука.',
            )}
          </Alert>
        )}

        {exerciseId === null && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {t('exercises.editor.dictationSaveFirst', 'Сначала сохраните задание: озвучивается текст, уже лежащий на сервере')}
          </Alert>
        )}

        {exerciseId !== null && unsavedText && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t(
              'exercises.editor.dictationUnsavedText',
              'Текст изменён и ещё не сохранён — озвучка недоступна. Сохраните задание: иначе озвучилась бы прежняя фраза, а деньги списались бы как за новую.',
            )}
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<GraphicEqIcon />}
            disabled={disabled || busy || sourceText.trim() === ''}
            onClick={() => void showPrice()}
          >
            {t('exercises.editor.dictationEstimate', 'Оценить стоимость')}
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={<RecordVoiceOverIcon />}
            disabled={disabled || busy || exerciseId === null || sourceText.trim() === '' || unsavedText}
            onClick={() => void synthesize()}
          >
            {busy
              ? t('exercises.editor.dictationSynthesizing', 'Озвучиваем…')
              : audio.status === 'ready'
                ? t('exercises.editor.dictationResynthesize', 'Озвучить заново')
                : t('exercises.editor.dictationSynthesize', 'Озвучить')}
          </Button>
        </Stack>

        {estimate && (
          <Alert severity={estimate.sufficient ? 'info' : 'warning'} sx={{ mt: 1 }}>
            {estimate.cached
              ? t('exercises.editor.dictationPriceCached', 'Фраза уже в кеше озвучки: {{price}} {{currency}}', {
                  price: estimate.price,
                  currency: estimate.currency,
                })
              : t(
                  'exercises.editor.dictationPrice',
                  '{{price}} {{currency}} за {{chars}} символов',
                  { price: estimate.price, currency: estimate.currency, chars: estimate.chars },
                )}{' '}
            {estimate.sufficient
              ? t('exercises.editor.dictationBalance', 'На балансе {{balance}}', { balance: estimate.balance })
              : t('exercises.editor.dictationBalanceLow', 'На балансе {{balance}} — не хватает, пополните счёт', {
                  balance: estimate.balance,
                })}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          {audio.status === 'ready' && audio.url ? (
            // Прослушивание прямо в форме: иначе учитель узнаёт о неверном
            // ударении или обрезанной фразе только со слов ученика
            <Box component="audio" controls src={audio.url} sx={{ width: '100%', maxWidth: 420 }} />
          ) : audio.status === 'failed' ? (
            <Alert severity="error">
              {t('exercises.editor.dictationAudioFailed', 'Озвучка не удалась, деньги не списаны — попробуйте ещё раз')}
            </Alert>
          ) : (
            <Alert severity="warning">
              {t('exercises.editor.dictationAudioNone', 'Записи нет: без озвучки ученик откроет диктант без звука')}
            </Alert>
          )}
        </Box>
      </Box>
    </Stack>
  )
}
